import startCase from "lodash/startCase";
import toLower from "lodash/toLower";

import { AssetClass } from "@vega-api/generated/types/assetClass";
import { type Permission } from "@vega-api/generated/types/permission";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { type UserPermissionEntry } from "@vega-api/generated/types/userPermissionEntry";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { TabNavigation } from "@vega-core/components/Layout/TabNavigation";
import { ModalHeader } from "@vega-core/components/Modals/ModalHeader/ModalHeader";
import { VegaDialog } from "@vega-core/components/Modals/VegaDialog/VegaDialog";
import { type Tab } from "@vega-core/models/core.type";
import { m } from "@vega-core/paraglide/messages";

import { FundLogos } from "../../../../../../components/AssetClassLogos/AssetClassImages";

import { HiddenPermissionsGrouped } from "./components/HiddenPermissionsGrouped";
import { PermissionsList } from "./components/PermissionsList";

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onReset: VoidFunction;
  role: RoleNew;
  icon?: string;
  subjectName: string;
  onPermissionToggle: (permissionId: string, enabled: boolean) => void;
  currentPermissions: Record<string, Permission> | undefined;
  isLoading?: boolean;
  isLP?: boolean;
  togglingPermission?: string | null;
  subjectPermissions?: UserPermissionEntry[];
};

export const EditRoleModal = ({
  open,
  onClose,
  role,
  subjectName,
  isLP,
  onPermissionToggle,
  currentPermissions,
  onReset,
  isLoading = false,
  subjectPermissions,
}: Props) => {
  const effectivePermissions =
    subjectPermissions && subjectPermissions.length > 0
      ? role.permissions.filter((permission) =>
          subjectPermissions.some((sp) => sp.permissionId === permission.id),
        )
      : role.permissions;

  const getContexts = (permission: Permission) =>
    currentPermissions?.[permission.id]?.contexts ?? permission.contexts ?? [];

  const USER_ACCESS_MANAGEMENT_CONTEXT =
    "WORKBENCH/FUNDS/INVESTMENTS/USER-ACCESS-MANAGEMENT";
  const HIDDEN_PERMISSION_CONTEXT =
    "WORKBENCH/FUNDS/INVESTMENTS/USER-ACCESS-MANAGEMENT/HIDDEN-PERMISSION";
  const SMA_GROUP_CONTEXT =
    "WORKBENCH/FUNDS/INVESTMENTS/USER-ACCESS-MANAGEMENT/HIDDEN-PERMISSION/SMA-GROUP-PERMISSION";

  // Only show permissions in the User Access Management scope.
  const userAccessManagementPermissions = effectivePermissions.filter(
    (permission) => {
      const contexts = getContexts(permission);

      return (
        contexts.includes(USER_ACCESS_MANAGEMENT_CONTEXT) ||
        contexts.includes(HIDDEN_PERMISSION_CONTEXT) ||
        contexts.includes(SMA_GROUP_CONTEXT)
      );
    },
  );

  const visiblePermissions = userAccessManagementPermissions.filter(
    (permission) => {
      const contexts = getContexts(permission);

      return (
        contexts.includes(USER_ACCESS_MANAGEMENT_CONTEXT) &&
        !contexts.includes(HIDDEN_PERMISSION_CONTEXT) &&
        !contexts.includes(SMA_GROUP_CONTEXT)
      );
    },
  );

  const hiddenPermissions = userAccessManagementPermissions.filter(
    (permission) => {
      const contexts = getContexts(permission);

      return (
        contexts.includes(HIDDEN_PERMISSION_CONTEXT) &&
        !contexts.includes(SMA_GROUP_CONTEXT)
      );
    },
  );

  const smaGroupPermissions = userAccessManagementPermissions.filter(
    (permission) => {
      const contexts = getContexts(permission);

      return contexts.includes(SMA_GROUP_CONTEXT);
    },
  );

  const tabs: Tab[] = [
    {
      index: "permissions",
      title: "Permissions",
      component: (
        <PermissionsList
          currentPermissions={currentPermissions}
          onPermissionToggle={onPermissionToggle}
          permissions={visiblePermissions}
        />
      ),
    },
    {
      index: "hidden-permissions",
      title: "Hidden Permissions",
      component: (
        <HiddenPermissionsGrouped
          currentPermissions={currentPermissions}
          hiddenPermissions={hiddenPermissions}
          onPermissionToggle={onPermissionToggle}
          smaGroupPermissions={smaGroupPermissions}
        />
      ),
    },
  ];

  return (
    <VegaDialog data-testid="edit-role-dialog" onClose={onClose} open={open}>
      <ModalHeader title={m.userManagement_permissions_customizeRole()} />
      <div className="body-base-regular text-text/secondary">
        <p>{m.userManagement_permissions_editRoleDescription()}</p>
        <p>
          <span className="body-base-semibold text-text/primary">
            {startCase(toLower(role.name))}
          </span>
        </p>
      </div>
      <div className="py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex flex-shrink-0 rounded-lg justify-center items-center overflow-hidden">
            <FundLogos
              assetClass={AssetClass.PRIVATE_EQUITY}
              isLoading={false}
            />
          </div>
          <div className="body-base-semibold text-text/primary">
            {subjectName}
          </div>
        </div>
        <ThemedButton
          dataTestId="reset-button"
          disabled={isLoading}
          onClick={onReset}
          variant="text"
        >
          {m.btn_reset()}
        </ThemedButton>
      </div>
      {!isLP ? (
        <TabNavigation noMargin tabs={tabs} />
      ) : (
        <div className="mt-4">
          <PermissionsList
            currentPermissions={currentPermissions}
            onPermissionToggle={onPermissionToggle}
            permissions={visiblePermissions}
          />
        </div>
      )}
    </VegaDialog>
  );
};
